export interface MenuItem {
  key: string;
  label: string;
  icon?: string;
  path?: string;
  requiredPermissions: string[];
  children?: MenuItem[];
  isHidden?: boolean; // Flag to mark menus that should be hidden by default
}

export const menuConfig: MenuItem[] = [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: "🏠",
    path: "/dashboard",
    requiredPermissions: [], // Dashboard is accessible to all authenticated users
  },
  {
    key: "projects",
    label: "Projects",
    icon: "🏗️",
    path: "/projects",
    requiredPermissions: ["project.view"],
  },
  // Units menu removed - access units through project management
  {
    key: "bookings",
    label: "Booking Management",
    icon: "📋",
    requiredPermissions: ["booking.view"],
    children: [
      {
        key: "active-bookings",
        label: "Active Bookings",
        path: "/bookings",
        requiredPermissions: ["booking.view"],
      },
      {
        key: "booking-history",
        label: "Booking History",
        path: "/booking/history",
        requiredPermissions: ["booking.history"],
      },
      {
        key: "cancelled-bookings",
        label: "Cancelled Bookings",
        path: "/booking/cancelled",
        requiredPermissions: ["booking.view"],
      },
      {
        key: "transferred-bookings",
        label: "Transferred Bookings",
        path: "/booking/transferred",
        requiredPermissions: ["booking.view"],
      },
    ],
  },
  {
    key: "financials",
    label: "Financial Management",
    icon: "💰",
    requiredPermissions: ["income.view", "expense.view"], // OR condition - needs at least one
    children: [
      {
        key: "income",
        label: "Income",
        path: "/income",
        requiredPermissions: ["income.view"],
      },
      {
        key: "expenses",
        label: "Expenses",
        path: "/expenses",
        requiredPermissions: ["expense.view"],
        isHidden: true,
      },
      {
        key: "other-income",
        label: "Other Income",
        path: "/other-income",
        requiredPermissions: ["income.view"],
        isHidden: true,
      },
      {
        key: "receivable-income",
        label: "Receivable Income",
        path: "/hidden-income",
        requiredPermissions: ["income.view"],
        isHidden: true,
      },
      {
        key: "partner-income",
        label: "Partner Income",
        path: "/partner-income",
        requiredPermissions: ["*", "superadmin", "partner.view"], // Superadmin OR partner.view (route checks both partner.view AND income.view)
        isHidden: true,
      },
      {
        key: "booking-summary",
        label: "Booking Summary",
        path: "/booking-summary",
        requiredPermissions: ["*", "superadmin", "booking.view"], // Super admin OR users with booking.view
        isHidden: true,
      },
      {
        key: "cancel-summary",
        label: "Cancel Summary",
        path: "/cancel-summary",
        requiredPermissions: ["*", "superadmin", "booking.view"], // Super admin OR users with booking.view
        isHidden: true,
      },
      {
        key: "upload-data",
        label: "Upload Data",
        path: "/upload-data",
        requiredPermissions: ["*", "superadmin"], // Super admin only
        isHidden: true,
      },
      {
        key: "historical-upload",
        label: "Historical Upload",
        path: "/historical-upload",
        requiredPermissions: ["*", "superadmin"], // Super admin only
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
      {
        key: "return-cancel-income",
        label: "Cancel Income Return",
        path: "/return-cancel-income",
        requiredPermissions: ["income.view"],
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
      {
        key: "cancel-receivable-income",
        label: "Cancel Receivable Return",
        path: "/cancel-receivable-income",
        requiredPermissions: ["income.view"],
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
    ],
  },
  {
    key: "partners",
    label: "Partners",
    icon: "🤝",
    path: "/partners",
    requiredPermissions: ["*", "superadmin", "partner.view"], // Superadmin OR partner.view
    isHidden: true,
  },
  {
    key: "brokers",
    label: "Brokers",
    icon: "🏘️",
    path: "/brokers",
    requiredPermissions: ["*", "superadmin", "broker.view"], // Superadmin OR broker.view
    isHidden: true,
  },
  {
    key: "authorization",
    label: "Authorization",
    icon: "🔐",
    requiredPermissions: [
      "booking.authorize",
      "income.approve",
      "*",
      "superadmin",
    ], // OR condition - needs at least one
    children: [
      {
        key: "booking-authorization",
        label: "Booking Authorization",
        path: "/approval/booking",
        requiredPermissions: ["booking.authorize"],
      },
      {
        key: "income-authorization",
        label: "Income Authorization",
        path: "/approval/income",
        requiredPermissions: ["income.approve"],
      },
      {
        key: "hidden-income-authorization",
        label: "Receivable Income Authorization",
        path: "/approval/hidden-income",
        requiredPermissions: ["superadmin"], // Super admin only
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
    ],
  },
  {
    key: "administration",
    label: "Administration",
    icon: "⚙️",
    requiredPermissions: ["user.view", "role.view"], // OR condition
    children: [
      {
        key: "users",
        label: "User Management",
        path: "/auth/user",
        requiredPermissions: ["user.view"],
      },
      {
        key: "roles",
        label: "Role Management",
        path: "/auth/roles",
        requiredPermissions: ["role.view"],
      },
      {
        key: "permissions",
        label: "Permissions",
        path: "/auth/permissions",
        requiredPermissions: ["role.view"], // Permissions are viewed within role management
        isHidden: true,
      },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    icon: "📊",
    requiredPermissions: ["report.view"],
    children: [
      {
        key: "unit-report",
        label: "Unit Report",
        path: "/reports/unit",
        requiredPermissions: ["report.unit"],
      },
      {
        key: "income-report",
        label: "Income Report",
        path: "/reports/income",
        requiredPermissions: ["report.income"],
      },
      {
        key: "follow-up-report",
        label: "Follow-Up Report",
        path: "/reports/follow-up",
        requiredPermissions: ["report.followup"],
      },
      {
        key: "receivable-report",
        label: "Receivable Report",
        path: "/reports/receivable",
        requiredPermissions: ["report.receivable"],
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
      {
        key: "combined-income-report",
        label: "Combined Income Report",
        path: "/reports/combined-income",
        requiredPermissions: ["report.combined"],
        isHidden: true, // Hidden context - only visible with Ctrl+Shift+6
      },
      {
        key: "booking-report",
        label: "Booking Report",
        path: "/reports/booking",
        requiredPermissions: ["report.view"],
      },
    ],
  },
];
